/**
 * Product Insights — AI-generated, evidence-backed explanations for a product.
 *
 * Two tiers: a headline for every Insight, and full details generated server-side
 * the first time someone opens it (critical urgency is generated up front).
 * Generation, grounding, cost tracking and spend limits all live in
 * product-service; the browser never calls a model.
 */
import { API_BASE_URL, authenticatedProApiFetch, ProApiClientError } from "./pro-api.client";

export type InsightKind = "alert_explanation" | "performance" | "optimization" | "resilience";
export type InsightUrgency = "critical" | "high" | "medium" | "low" | "info";
export type InsightStatus = "open" | "resolved" | "dismissed";
export type InsightConfidence = "high" | "medium" | "low";
export type InsightFeedback = "helpful" | "not_helpful";

export interface InsightEvidenceWindow {
  start: string;
  end: string;
  keys?: string[];
}

export interface InsightEvidence {
  label: string;
  detail?: string;
  /** Telemetry window backing this claim; the UI links it to Historical Data. */
  window?: InsightEvidenceWindow;
}

export interface InsightSource {
  kind: "knowledge_base" | "telemetry" | "alert_history" | "external";
  label: string;
  ref?: string;
}

/** Model, tokens and cost for one generation. Only returned to super-admins. */
export interface InsightGenerationMeta {
  model: string;
  promptVersion: string;
  generatedAt: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  costUsd: number;
}

export interface InsightDetails {
  confidence: InsightConfidence;
  evidence: InsightEvidence[];
  selfServiceSteps: string[];
  contactLionIf: string[];
  sources: InsightSource[];
  generation: InsightGenerationMeta | null;
}

export interface ProductInsight {
  id: string;
  productId: string;
  organizationId: string | null;
  kind: InsightKind;
  status: InsightStatus;
  urgency: InsightUrgency;
  title: string;
  summary: string;
  alertCode: string | null;
  occurrenceCount: number;
  createdAt: string;
  resolvedAt: string | null;
  expiresAt: string | null;
  /** The current viewer's own vote. */
  feedback: InsightFeedback | null;
  /** Null for templated headlines and for non-super-admins. */
  headlineGeneration: InsightGenerationMeta | null;
  /** Null until someone opens the Insight (critical urgency is generated up front). */
  details: InsightDetails | null;
}

export interface InsightUsageSummary {
  period: { start: string; end: string };
  headlines: { count: number; templated: number; costUsd: number };
  details: { count: number; costUsd: number };
  /** Headlines nobody opened, so their full analysis was never generated. */
  detailsSkipped: { count: number; estimatedCostAvoidedUsd: number };
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  costUsd: number;
}

/** How the site is doing, as the customer reports it when asking for help. */
export type InsightSupportImpact = "down" | "degraded" | "working";

/** Triage details collected by the "Still need help" modal. */
export interface InsightSupportFields {
  impact: InsightSupportImpact;
  stepsTried: string[];
  notes: string | null;
  callbackPhone: string | null;
}

export interface InsightSupportRequest {
  /** Short reference (e.g. INS-4F2K9) the customer can quote to Lion support. */
  reference: string;
  /** "recorded" until HubSpot ticket delivery is live. */
  deliveryStatus: "recorded" | "delivered" | "failed";
}

export interface LedgerTotals {
  count: number;
  /** Calls that returned an error; usually billed $0. */
  failed: number;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
}

export interface OrgInsightUsage {
  period: { start: string; end: string };
  organizations: Array<{
    organizationId: string | null;
    totals: LedgerTotals;
    byTier: Record<"headline" | "details", LedgerTotals>;
    byModel: Record<string, LedgerTotals>;
    byProduct: Array<{ productId: string; totals: LedgerTotals }>;
  }>;
}

export interface InsightBudgetStatus {
  enabled: boolean;
  redisAvailable: boolean;
  day: { start: string; spentUsd: number; limitUsd: number };
  month: { start: string; spentUsd: number; limitUsd: number };
  orgMonthlyLimitUsd: number;
  /** Per feature area (insights, localization): its own kill switch and monthly budget. */
  areas: Array<{ area: string; enabled: boolean; spentUsd: number; limitUsd: number }>;
}

/** Error codes the details endpoint returns when analysis can't be shown. */
export type InsightDetailsErrorCode =
  | "no_guidance"
  | "ai_not_configured"
  | "generation_failed"
  | "busy"
  | "budget_exceeded";

export function insightErrorCode(err: unknown): string | null {
  if (err instanceof ProApiClientError && err.data && typeof err.data.code === "string") return err.data.code;
  return null;
}

// ─── Product-scoped ─────────────────────────────────────────────────────────

export async function fetchProductInsights(
  accessToken: string,
  productId: string,
): Promise<{ data: ProductInsight[] }> {
  return authenticatedProApiFetch(`/products/${productId}/insights?status=open,resolved`, accessToken);
}

/** Explanation for one alert code, or null when no Insight exists for it yet. */
export async function fetchAlertInsight(
  accessToken: string,
  productId: string,
  alertCode: string,
): Promise<ProductInsight | null> {
  const res = await authenticatedProApiFetch<{ data: ProductInsight | null }>(
    `/products/${productId}/insights?alertCode=${encodeURIComponent(alertCode)}`,
    accessToken,
  );
  return res.data;
}

/** Returns the full analysis, generating it on first request. Also the "opened" signal. */
export async function fetchInsightDetails(
  accessToken: string,
  productId: string,
  insightId: string,
): Promise<ProductInsight> {
  return authenticatedProApiFetch(`/products/${productId}/insights/${insightId}/details`, accessToken, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function submitInsightFeedback(
  accessToken: string,
  productId: string,
  insightId: string,
  feedback: InsightFeedback,
): Promise<ProductInsight> {
  return authenticatedProApiFetch(`/products/${productId}/insights/${insightId}/feedback`, accessToken, {
    method: "POST",
    body: JSON.stringify({ feedback }),
  });
}

export async function dismissInsight(accessToken: string, productId: string, insightId: string): Promise<void> {
  await authenticatedProApiFetch(`/products/${productId}/insights/${insightId}/dismiss`, accessToken, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** Records a support request with the Insight attached, so support starts with context. */
export async function requestInsightSupport(
  accessToken: string,
  productId: string,
  insightId: string,
  fields: InsightSupportFields,
): Promise<InsightSupportRequest> {
  return authenticatedProApiFetch(`/products/${productId}/insights/${insightId}/support-request`, accessToken, {
    method: "POST",
    body: JSON.stringify(fields),
  });
}

/** Support request for an alert that has no Insight (no approved guidance yet). */
export async function requestAlertSupport(
  accessToken: string,
  productId: string,
  alertCode: string,
  fields: InsightSupportFields,
): Promise<InsightSupportRequest> {
  return authenticatedProApiFetch(`/products/${productId}/insights/support-request`, accessToken, {
    method: "POST",
    body: JSON.stringify({ ...fields, alertCode }),
  });
}

/** Super-admin only. */
export async function fetchInsightUsage(accessToken: string, productId: string): Promise<InsightUsageSummary> {
  return authenticatedProApiFetch(`/products/${productId}/insights/usage?period=month`, accessToken);
}

// ─── System (super-admin) ───────────────────────────────────────────────────

function periodQuery(period: { start?: string; end?: string }): string {
  const qs = new URLSearchParams();
  if (period.start) qs.set("start", period.start);
  if (period.end) qs.set("end", period.end);
  return qs.toString();
}

export async function fetchOrgInsightUsage(
  accessToken: string,
  period: { start?: string; end?: string } = {},
): Promise<OrgInsightUsage> {
  const qs = periodQuery(period);
  return authenticatedProApiFetch(qs ? `/insights/usage?${qs}` : "/insights/usage", accessToken);
}

export async function fetchInsightBudget(accessToken: string): Promise<InsightBudgetStatus> {
  return authenticatedProApiFetch("/insights/budget", accessToken);
}

/** Downloads the per-org, per-product usage CSV for the period. */
export async function downloadOrgInsightUsageCsv(
  accessToken: string,
  period: { start?: string; end?: string } = {},
): Promise<void> {
  const qs = new URLSearchParams(periodQuery(period));
  qs.set("format", "csv");
  const res = await fetch(`${API_BASE_URL}/insights/usage?${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new ProApiClientError("Failed to export usage", res.status);
  const blob = await res.blob();
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = match?.[1] ?? "insights-usage.csv";
  a.click();
  URL.revokeObjectURL(url);
}

/** The subset of this module the Insights components call; swappable in Storybook. */
export type InsightsApi = Pick<
  typeof import("./insights"),
  | "fetchProductInsights"
  | "fetchAlertInsight"
  | "fetchInsightDetails"
  | "submitInsightFeedback"
  | "dismissInsight"
  | "requestInsightSupport"
  | "requestAlertSupport"
  | "fetchInsightUsage"
>;

// ─── Support inbox (super-admin) ────────────────────────────────────────────

export type SupportInboxStatus = "open" | "handled" | "all";

export interface SupportInboxRow {
  id: string;
  reference: string;
  productId: string;
  productName: string | null;
  organizationId: string | null;
  insightId: string | null;
  insightTitle: string | null;
  insightUrgency: InsightUrgency | null;
  alertCode: string | null;
  impact: InsightSupportImpact | null;
  stepsTried: string[];
  notes: string | null;
  callbackPhone: string | null;
  requestedByUserId: string;
  requestedByName: string | null;
  requestedByEmail: string | null;
  deliveryStatus: InsightSupportRequest["deliveryStatus"];
  createdAt: string;
  handledAt: string | null;
  handledByUserName: string | null;
  handledNote: string | null;
}

export interface SupportRequestDetail extends SupportInboxRow {
  insight: ProductInsight | null;
}

export async function fetchSupportRequests(
  accessToken: string,
  params: { status: SupportInboxStatus; search?: string; page: number; limit: number; order?: "asc" | "desc" },
): Promise<{ data: SupportInboxRow[]; total: number }> {
  const qs = new URLSearchParams({
    status: params.status,
    page: String(params.page),
    limit: String(params.limit),
    order: params.order ?? "desc",
  });
  if (params.search?.trim()) qs.set("search", params.search.trim());
  return authenticatedProApiFetch(`/insights/support-requests?${qs}`, accessToken);
}

/** By id or INS- reference. */
export async function fetchSupportRequest(accessToken: string, idOrReference: string): Promise<SupportRequestDetail> {
  return authenticatedProApiFetch(`/insights/support-requests/${encodeURIComponent(idOrReference)}`, accessToken);
}

export async function markSupportRequestHandled(
  accessToken: string,
  id: string,
  note: string | null,
): Promise<SupportRequestDetail> {
  return authenticatedProApiFetch(`/insights/support-requests/${id}/handle`, accessToken, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function reopenSupportRequest(accessToken: string, id: string): Promise<SupportRequestDetail> {
  return authenticatedProApiFetch(`/insights/support-requests/${id}/reopen`, accessToken, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// ─── Alert guidance authoring (super-admin) ─────────────────────────────────

export type AlertGuidanceStatus = "draft" | "in_review" | "approved";

export interface AlertGuidance {
  id: string;
  catalogAlertId: string;
  summary: string;
  likelyCauses: string[];
  selfServiceSteps: string[];
  contactLionIf: string[];
  evidenceKeys: string[];
  status: AlertGuidanceStatus;
  authoredByUserId: string;
  authoredByName: string | null;
  approvedByUserId: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  /** Version Insights currently use; older than `version` while an edit is pending. */
  liveVersion: number | null;
  reviewNote: string | null;
  reviewedByName: string | null;
  version: number;
  updatedAt: string;
}

export interface GuidanceListRow {
  catalogAlertId: string;
  catalogId: string;
  catalogName: string;
  code: string;
  name: string;
  type: string;
  severity: number;
  incidentCount90d: number;
  guidance: {
    status: AlertGuidanceStatus;
    version: number;
    authoredByUserId: string;
    authoredByName: string | null;
    updatedAt: string;
    hasReviewNote: boolean;
    liveVersion: number | null;
  } | null;
}

export interface GuidanceDetail {
  alert: {
    id: string;
    catalogId: string;
    catalogName: string | null;
    code: string;
    name: string;
    type: string;
    severity: number;
    description: string | null;
    incidentCount90d: number;
  };
  guidance: AlertGuidance | null;
  otherCatalogs: Array<{ catalogAlertId: string; catalogId: string; catalogName: string; guidance: AlertGuidance | null }>;
}

export interface GuidanceContent {
  summary: string;
  likelyCauses: string[];
  selfServiceSteps: string[];
  contactLionIf: string[];
  evidenceKeys: string[];
}

export async function fetchGuidanceList(
  accessToken: string,
  params: {
    status?: AlertGuidanceStatus | "none" | "any";
    catalogId?: string;
    type?: "alarm" | "fault";
    search?: string;
    sort?: "incidents" | "code" | "updated";
    page: number;
    limit: number;
  },
): Promise<{ data: GuidanceListRow[]; total: number }> {
  const qs = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  if (params.status) qs.set("status", params.status);
  if (params.catalogId) qs.set("catalogId", params.catalogId);
  if (params.type) qs.set("type", params.type);
  if (params.sort) qs.set("sort", params.sort);
  if (params.search?.trim()) qs.set("search", params.search.trim());
  return authenticatedProApiFetch(`/alert-guidance?${qs}`, accessToken);
}

export async function fetchGuidance(accessToken: string, catalogAlertId: string): Promise<GuidanceDetail> {
  return authenticatedProApiFetch(`/alert-guidance/${catalogAlertId}`, accessToken);
}

/** Saves a draft, or submits for review. `expectedVersion` is the version loaded (null when creating). */
export async function saveGuidance(
  accessToken: string,
  catalogAlertId: string,
  content: GuidanceContent,
  opts: { submitForReview: boolean; expectedVersion: number | null },
): Promise<AlertGuidance> {
  return authenticatedProApiFetch(`/alert-guidance/${catalogAlertId}`, accessToken, {
    method: "PUT",
    body: JSON.stringify({ ...content, ...opts }),
  });
}

export async function approveGuidance(
  accessToken: string,
  catalogAlertId: string,
  expectedVersion: number,
): Promise<AlertGuidance> {
  return authenticatedProApiFetch(`/alert-guidance/${catalogAlertId}/approve`, accessToken, {
    method: "POST",
    body: JSON.stringify({ expectedVersion }),
  });
}

export async function requestGuidanceChanges(
  accessToken: string,
  catalogAlertId: string,
  expectedVersion: number,
  note: string,
): Promise<AlertGuidance> {
  return authenticatedProApiFetch(`/alert-guidance/${catalogAlertId}/request-changes`, accessToken, {
    method: "POST",
    body: JSON.stringify({ expectedVersion, note }),
  });
}

// ─── Pilot metrics (super-admin) ────────────────────────────────────────────

/** Rates are null when there was nothing to divide by. */
export interface InsightMetrics {
  period: { start: string; end: string };
  organizationId: string | null;
  engagement: {
    created: number;
    opened: number;
    openRate: number | null;
    openedWithAnalysis: number;
    openedWithoutAnalysis: number;
    dismissedByUser: number;
    expiredUnopened: number;
    medianMinutesToOpen: number | null;
  };
  feedback: { helpful: number; notHelpful: number; helpfulRate: number | null };
  support: {
    requests: number;
    fromInsights: number;
    perOpenedInsight: number | null;
    byImpact: Record<InsightSupportImpact, number>;
    handled: number;
    medianHoursToHandle: number | null;
  };
  cost: { costUsd: number; calls: number; failedCalls: number; perOpenedInsightUsd: number | null };
  codes: Array<{
    code: string;
    insights: number;
    opened: number;
    openRate: number | null;
    openedWithoutAnalysis: number;
    helpful: number;
    notHelpful: number;
    helpfulRate: number | null;
    supportRequests: number;
    hasGuidance: boolean;
  }>;
  notHelpful: Array<{
    insightId: string;
    productId: string;
    productName: string | null;
    title: string;
    alertCode: string | null;
    createdAt: string;
    notHelpful: number;
    helpful: number;
  }>;
}

export async function fetchInsightMetrics(
  accessToken: string,
  params: { start?: string; end?: string; organizationId?: string },
): Promise<InsightMetrics> {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.organizationId) qs.set("organizationId", params.organizationId);
  const query = qs.toString();
  return authenticatedProApiFetch(query ? `/insights/metrics?${query}` : "/insights/metrics", accessToken);
}
